import { listApiData, postApiData } from "@hooks/utils/api";
import { catchSentryError } from "@utils/sentry";
import { getPublicImageUrl } from "@utils/storage";
import { useCallback, useEffect, useState } from "react";
import { decodeNotification } from "catchment-common/decoders/notification";
import { Notification } from "catchment-common/entities/notification";
import { NotificationLineItem } from "catchment-common/interfaces/notification";
import { ProfileLineItem } from "catchment-common/interfaces/profile";

export type NotificationContext =
  | "organisation"
  | "project"
  | "package"
  | "shared-project";

interface UseNotificationOptions {
  context: NotificationContext;
  projectId?: string;
  contractorPackageId?: string;
  organisationId?: string;
  limit?: number;
  offset?: number;
  profile?: ProfileLineItem;
}

export const useNotification = (options: UseNotificationOptions) => {
  const { context, contractorPackageId, organisationId, profile, projectId } =
    options;
  const [notifications, setNotifications] = useState<NotificationLineItem[]>(
    []
  );
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchNotifications = useCallback(
    async (limit: number = 20, offset: number = 0) => {
      if (!profile) return;

      try {
        setIsLoading(true);
        setError(null);

        let notificationData: NotificationLineItem[] = [];

        if (context === "organisation" && organisationId) {
          const res = await listApiData(
            "listNotificationsByOrganisationWithUserStatus",
            "notification",
            organisationId,
            { limit: String(limit), offset: String(offset) }
          );
          notificationData = await Promise.all(
            ((res.data ?? []) as Notification[]).map((n) =>
              decodeNotification(n, getPublicImageUrl)
            )
          );
        } else if (context === "project" && projectId) {
          const res = await listApiData(
            "listNotificationsByProjectWithUserStatus",
            "notification",
            projectId,
            { limit: String(limit), offset: String(offset) }
          );
          notificationData = await Promise.all(
            ((res.data ?? []) as Notification[]).map((n) =>
              decodeNotification(n, getPublicImageUrl)
            )
          );
        } else if (context === "package" && contractorPackageId) {
          const res = await listApiData(
            "listNotificationsByPackageWithUserStatus",
            "notification",
            contractorPackageId,
            { limit: String(limit), offset: String(offset) }
          );
          notificationData = await Promise.all(
            ((res.data ?? []) as Notification[]).map((n) =>
              decodeNotification(n, getPublicImageUrl)
            )
          );
        } else if (context === "shared-project" && projectId) {
          // For shared project, only show PROJECT_REPORT_SUBMITTED type
          const res = await listApiData(
            "listNotificationsBySharedProjectWithUserStatus",
            "notification",
            projectId,
            { limit: String(limit), offset: String(offset) }
          );
          notificationData = await Promise.all(
            ((res.data ?? []) as Notification[]).map((n) =>
              decodeNotification(n, getPublicImageUrl)
            )
          );
        }

        if (offset === 0) {
          setNotifications(notificationData);
        } else {
          setNotifications((prev) => [...prev, ...notificationData]);
        }
        setHasMore(notificationData.length === limit);
      } catch (e) {
        catchSentryError(e);
        setError("Could not fetch notifications");
      } finally {
        setIsLoading(false);
      }
    },
    [
      context,
      projectId,
      contractorPackageId,
      organisationId,
      profile,
      getPublicImageUrl
    ]
  );

  const loadMore = useCallback(() => {
    if (!isLoading && hasMore) {
      fetchNotifications(20, notifications.length);
    }
  }, [notifications.length, hasMore, isLoading, fetchNotifications]);

  const markAsViewed = useCallback(async (notificationIds: string[]) => {
    try {
      await postApiData("markNotificationsAsViewed", "notification", {
        notificationIds
      } as any);
      setNotifications((prev) =>
        prev.map((n) =>
          notificationIds.includes(n.id) ? { ...n, userViewed: true } : n
        )
      );
    } catch (e) {
      catchSentryError(e);
      setError("Could not mark notifications as viewed");
    }
  }, []);

  const clearNotification = useCallback(async (notificationId: string) => {
    try {
      await postApiData("deleteNotificationsFromUserView", "notification", {
        notificationIds: [notificationId]
      } as any);
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
    } catch (e) {
      catchSentryError(e);
      setError("Could not clear notification");
    }
  }, []);

  const clearAllNotifications = useCallback(async () => {
    try {
      const idsToClear = notifications.map((n) => n.id);
      await postApiData("deleteNotificationsFromUserView", "notification", {
        notificationIds: idsToClear
      } as any);
      setNotifications([]);
    } catch (e) {
      catchSentryError(e);
      setError("Could not clear all notifications");
    }
  }, [notifications]);

  const refreshNotifications = useCallback(() => {
    setNotifications([]);
    setHasMore(true);
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    // Reset notifications when context changes
    setNotifications([]);
    setHasMore(true);

    // Only fetch if we have the required IDs
    const shouldFetch =
      (context === "organisation" && organisationId) ||
      (context === "project" && projectId) ||
      (context === "package" && contractorPackageId) ||
      (context === "shared-project" && projectId);

    if (shouldFetch && profile) {
      fetchNotifications();
    }
  }, [
    context,
    projectId,
    contractorPackageId,
    organisationId,
    fetchNotifications,
    profile
  ]);

  useEffect(() => {
    // Update unread count
    const unread = notifications.filter((n) => !n.userViewed).length;
    setUnreadCount(unread);
  }, [notifications]);

  return {
    clearAllNotifications,
    clearNotification,
    error,
    hasMore,
    isLoading,
    loadMore,
    markAsViewed,
    notifications,
    refreshNotifications,
    unreadCount
  };
};
